import React, { useContext, useEffect, useState } from 'react'
import Dialog from '~/components/Dialog'
import TableComponent from '~/components/TableComponent'
import { useAuth } from '~/routes/components/AuthProvider'
import { TermsWrapper } from '../../Dashboard/style'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import MilkFixedService, { AmountMonthType, AmountSettingsType, TradingSlotSettingsType } from '~/api/services/milkFixedPrice'
import { Checkbox, Segment } from 'semantic-ui-react'
import notify from '~/components/Notify'
import TwoFaComp from '~/components/TwoFaComp'
import ProfileSettingsService from '~/api/services/profileSettings'
import StockExChangeService from '~/api/services/stockExchange'
import { FixedPricePrefixType } from '~/api/services/dashboard'
import FatAndProteinService from '~/api/services/fatAndProtein'
import { handleErrors } from '~/utils/helpers'
import { useParams } from 'react-router-dom'

interface ConfirmModalProps {
  openConfirmModal: boolean
  offerAmounts: AmountMonthType[]
  offersPreviewColumns: any[]
  tradingSettings: TradingSlotSettingsType
  amounts: any[]
  handleClose: (navigate?: boolean) => void
  settingsAmounts: AmountSettingsType
  prefix?: string
}

const ConfirmModal: React.FC<ConfirmModalProps> = (props) => {
  const { id } = useParams()
  const { t } = useTranslation()
  const {
    openConfirmModal,
    offerAmounts,
    offersPreviewColumns,
    tradingSettings,
    handleClose,
    amounts,
    settingsAmounts,
    prefix
  } = props
  const { systemData } = useAuth()
  const { firstColor } = useContext(ThemeContext)
  const tfaOffer = systemData?.roleSettings?.tfaOffer === 1
  const tfaType = systemData?.logged?.tfaType

  const [tfaCode, setTfaCode] = useState('')
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(0)

  const termsNeedToBeChecked = Boolean(tradingSettings?.tradingRoom && tradingSettings?.terms?.length > 0)

  useEffect(() => {
    if (tfaOffer && openConfirmModal) {
      handleSendCode()
    }
  }, [openConfirmModal])

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  async function getResponse(fileName) {
    switch (prefix) {
      case FixedPricePrefixType.FatProtein:
        return FatAndProteinService.openTradingRoomFile({ fileName })
      case FixedPricePrefixType.FixedPrice:
        return MilkFixedService.openTradingRoomFile({ fileName })
      case FixedPricePrefixType.StockExchange:
        return StockExChangeService.openTradingRoomFile({ fileName })
      default:
        return MilkFixedService.openTradingRoomFile({ fileName })
    }
  }

  async function handleClickTermItem(fileName) {
    setIsBusy(true)
    try {
      const response = await getResponse(fileName)
      setFileSource(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderContenAcceptModal() {
    return <TermsWrapper firstColor={firstColor}>
      <div style={{ textAlign: 'center', marginBottom: 50 }} >
        <div className='note-title'>{t('Acceptance')}</div>
        <div className='title'>{t('Please check the:')}</div>
        {tradingSettings?.terms.map(term => {
          return term.visible && <div
            onClick={() => handleClickTermItem(term.filename)}
            className='item' key={term.title}
          >
            {term.title}
          </div>
        })}
        <div className='title'>{t('which you can see by clicking on each.')}</div>
        <Checkbox
          label={<label className='highlight-label'>
            {tradingSettings?.texts?.find(text => text.slug === 'submit_offer_text')?.translation || ''}
          </label>}
          checked={acceptTerms === 1}
          onChange={(e, data) => setAcceptTerms(data.checked ? 1 : 0)}
          style={{ marginTop: 20 }}
        />
      </div>
    </TermsWrapper>
  }

  async function submitOffer(id, data) {
    switch (prefix) {
      case FixedPricePrefixType.FatProtein:
        return FatAndProteinService.saveFarmerOfferAmountsList(id, { amounts, ...data })
      case FixedPricePrefixType.FixedPrice:
        return MilkFixedService.saveFarmerOfferAmountsList(id, { amounts, ...data })
      case FixedPricePrefixType.StockExchange:
        return StockExChangeService.saveFarmerOfferAmountsList(id, { amounts, ...data })
      default:
        return MilkFixedService.saveFarmerOfferAmountsList(id, { amounts, ...data })
    }
  }

  async function handleSave(data?) {
    setIsBusy(true)
    try {
      await submitOffer(id, data)
      notify(t('Offers confirmed'), 'success');
      handleClose(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderConfirmModal() {
    return <Dialog
      disableSubmit={tfaOffer ? termsNeedToBeChecked ? !((acceptTerms === 1) && correctTfaCode) : !correctTfaCode : termsNeedToBeChecked ? acceptTerms !== 1 : false}
      isDialogOpen={openConfirmModal}
      style={{ width: '75%' }}
      isLoading={isBusy}
      handleClose={() => {
        setTfaCode('')
        setCorrectTfaCode(false)
        setAcceptTerms(0)
        handleClose()
      }}
      textClose={t('Correct offered amount')}
      textSubmit={t('Confirm offered amount')}
      handleSubmit={() => handleSave(tfaOffer ? { code: tfaCode } : null)}
      textContent={<>
        <TableComponent
          tableColumnHeaders={offersPreviewColumns}
          rows={offerAmounts}
          removePagination
          options={{
            minimumContingent: settingsAmounts.minimumContingent
          }}
          total={offerAmounts.length}
        />
        <div>
          {tfaOffer && <TwoFaComp
            onChangeTfaCode={(code) => setTfaCode(code)}
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />}
        </div>
        {termsNeedToBeChecked && tradingSettings?.terms?.length > 0 && <Segment>
          {renderContenAcceptModal()}
        </Segment>
        }
      </>}
      textHeader={t('Offer amounts')}
      type={'dialog'}
    />
  }

  function renderPreviewModal() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  return <>
    {renderConfirmModal()}
    {renderPreviewModal()}
  </>
}
export default ConfirmModal